import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BaseAuthStore } from 'pocketbase';
import { PocketbaseService } from './pocketbase.service';
import { User } from '@shared/models';
import { CustomToastrService } from '@shared/services/index';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly customToastrService = inject(CustomToastrService);
  private readonly pocketbaseService = inject(PocketbaseService);
  private readonly router = inject(Router);

  readonly #authStore = signal<BaseAuthStore | null>(this.pocketbaseService.getPocketBase().authStore);
  readonly #user = signal<User | null>(this.pocketbaseService.getPocketBase().authStore.model as User | null);
  readonly #isAuthenticated = signal<boolean>(this.pocketbaseService.getPocketBase().authStore.isValid);

  readonly authStore = this.#authStore.asReadonly();

  readonly user = this.#user.asReadonly();
  readonly userRoles = computed(() => {
    const role = (this.user() as any)?.role;
    if (!role) return [];
    if (Array.isArray(role)) return role;
    if (typeof role === 'string') {
      return role
        .split(',')
        .map(r => r.trim())
        .filter(Boolean);
    }
    return [];
  })
  readonly isAuthenticated = this.#isAuthenticated.asReadonly();
  readonly isAdmin = computed(() => this.userRoles().includes('admin'));
  readonly isContentManager = computed(() => this.userRoles().includes('admin') || this.userRoles().includes('korrektor'));
  readonly isSilverUser = computed(() => this.userRoles().includes('silver'));
  readonly isGoldUser = computed(() => this.userRoles().includes('gold'));
  readonly isKorrektorUser = computed(() => this.userRoles().includes('korrektor'));
  readonly isGrader = computed(() => this.userRoles().includes('grader') || this.userRoles().includes('kanzlei_admin'));
  readonly isStudent = computed(() => this.userRoles().includes('student'));
  readonly isKanzleiAdmin = computed(() => this.userRoles().includes('kanzlei_admin'));
  readonly hasOrganization = computed(() => !!(this.user() as any)?.organization);
  readonly isVerified = computed(() => this.user()?.verified ?? false);
  readonly isOrgKorrektor = computed(() => (this.user() as any)?.org_role === 'korrektor');
  readonly isAuthor = computed(() => this.userRoles().includes('author'));
  readonly isApprovedAuthor = computed(() => {
    if (!this.isAuthor()) return false;
    const status = (this.user() as any)?.author_status;
    return status === 'approved' || status === 'trusted';
  });
  // Admins may open author views for debug/support.
  readonly canUseAuthorDashboard = computed(() => this.isAuthor() || this.isAdmin());
  readonly isOrgOwnerOrAdmin = computed(() => {
    const orgRole = (this.user() as any)?.org_role;
    return orgRole === 'owner' || orgRole === 'admin';
  });

  private refreshInFlight: Promise<void> | null = null;

  private syncFromAuthStore(): void {
    const pb = this.pocketbaseService.getPocketBase();
    this.#authStore.set(pb.authStore);
    this.#user.set(pb.authStore.model as User | null);
    this.#isAuthenticated.set(pb.authStore.isValid);
  }

  constructor() {
    // Ensure initial values reflect whatever is currently persisted in the PB authStore.
    this.syncFromAuthStore();

    this.pocketbaseService.getPocketBase().authStore.onChange(() => {
      this.syncFromAuthStore();
    });
  }

  needsProfileCompletion(): boolean {
    // Profile completion is relevant only for authenticated sessions.
    // PocketBase may return a record payload even when auth didn't complete (e.g. MFA step required),
    // but without a valid token we must treat the user as unauthenticated.
    if (!this.pocketbaseService.getPocketBase().authStore.isValid) {
      return false;
    }
    const user = this.user() as any;
    const hasPrivacyAcceptance = !!user?.privacy_policy_hash && !!user?.privacy_policy_accepted_date;
    const hasProfilePersona = !!user?.profileData?.role;
    return !hasProfilePersona || !hasPrivacyAcceptance;
  }

  // Login with email and password
  async login(email: string, password: string) {
    try {
      const result = await this.pocketbaseService.getPocketBase().collection('users').authWithPassword(email, password, {
        expand: 'organization'
      });

      // Signals are updated automatically via onChange listener
      this.syncFromAuthStore();
      return result;
    } catch (error) {
      this.customToastrService.showError('Login failed. Please try again.');

      throw new Error('Login failed. Please try again.');
    }
  }

  /** Zählt Logouts, damit ein noch laufender authRefresh die Session nicht wiederherstellt. */
  private logoutVersion = 0;

  // Logout current user
  logout() {
    this.logoutVersion++;
    this.pocketbaseService.getPocketBase().authStore.clear();
    // Signals are updated automatically via onChange listener
    this.syncFromAuthStore();
  }

  hasRole(role: string): boolean {
    return this.userRoles()?.includes(role);
  }

  async refreshAuth() {
    const pb = this.pocketbaseService.getPocketBase();
    if (!pb.authStore.isValid) return;

    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = (async () => {
      const doRefresh = async () => {
        const versionBefore = this.logoutVersion;
        await pb.collection('users').authRefresh({ expand: 'organization' });
        // Während des Refreshs ausgeloggt (z. B. Gast-Session beim Aufruf von
        // /auth/register beendet): Antwort verwerfen statt die Session zurückzuholen.
        if (versionBefore !== this.logoutVersion) {
          pb.authStore.clear();
        }
        this.syncFromAuthStore();
      };

      try {
        await doRefresh();
      } catch (err) {
        const status = (err as any)?.status ?? (err as any)?.response?.status;
        console.warn('Auth refresh failed', { status, err });

        if (status === 401) {
          // Retry once — may race with token persistence on fresh login
          await new Promise(resolve => setTimeout(resolve, 250));
          try {
            await doRefresh();
            return;
          } catch (err2) {
            const status2 = (err2 as any)?.status ?? (err2 as any)?.response?.status;
            console.warn('Auth refresh retry failed', { status: status2, err: err2 });
            if (status2 === 401) {
              this.logout();
            } else {
              this.syncFromAuthStore();
            }
          }
          return;
        }

        // Non-401 errors: keep the existing authStore session as-is.
        this.syncFromAuthStore();
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  /**
   * Unified post-login routing. Call after any successful authentication.
   */
  navigateAfterLogin(returnUrl?: string) {
    // 0. Pending redirect stored in PocketBase profileData (cross-device, e.g. kanzlei invite)
    const pb = this.pocketbaseService.getPocketBase();
    const profileData = pb.authStore.model?.['profileData'] as any;
    const pendingRedirect = profileData?.pending_redirect as string | undefined;
    if (pendingRedirect && pendingRedirect.startsWith('/') && !pendingRedirect.startsWith('/auth/')) {
      // Clear it so it's only used once
      pb.collection('users').update(pb.authStore.model!.id, {
        'profileData': { ...profileData, pending_redirect: null }
      }).catch(() => {}); // best-effort, don't block navigation
      this.router.navigateByUrl(pendingRedirect);
      return;
    }

    // 1. Pending course invitation
    const inviteToken = localStorage.getItem('invite_token');
    if (inviteToken) {
      localStorage.removeItem('invite_token');
      this.router.navigate(['/course/join', inviteToken], { replaceUrl: true });
      return;
    }

    // 2. Explicit return URL
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('/auth/')) {
      this.router.navigateByUrl(returnUrl);
      return;
    }

    // 3. Role-based routing
    // Authors (but not admins, who keep their existing dashboard) land on the author dashboard.
    if (this.isAuthor() && !this.isAdmin()) {
      this.router.navigate(['/author/dashboard']);
      return;
    }
    if (this.isGrader() || this.isAdmin()) {
      this.router.navigate(['/pilot/dashboard']);
    } else if (this.isKanzleiAdmin()) {
      this.router.navigate(['/kanzlei']);
    } else if (this.isKorrektorUser()) {
      this.router.navigate(['/']);
    } else {
      // Student or unknown role
      const pb = this.pocketbaseService.getPocketBase();
      const assignedCourses = pb.authStore.model?.['assigned_courses'] || [];
      if (assignedCourses.length > 0) {
        this.router.navigate(['/student/courses']);
      } else {
        this.router.navigate(['/']);
      }
    }
  }

  // Login with Google OAuth (Redirect Flow)
  async loginWithGoogle() {
    try {
      const pb = this.pocketbaseService.getPocketBase();
      const authMethods = await pb.collection('users').listAuthMethods() as any;

      if (!authMethods) {
        throw new Error('No auth methods returned from PocketBase.');
      }

      // Check where the providers are locally located
      // New PB versions: authMethods.oauth2.providers
      const providers = authMethods.oauth2?.providers || authMethods.authProviders || authMethods.providers || [];
      const googleProvider = providers.find((p: any) => p.name === 'google');

      if (!googleProvider) {
        throw new Error('Google authentication is not available.');
      }

      // 1. Save state and codeVerifier to localStorage for verification after redirect
      localStorage.setItem('pb_auth_state', googleProvider.state);
      localStorage.setItem('pb_auth_cv', googleProvider.codeVerifier);

      // 2. Define the redirect URL (must be the CURRENT PAGE or specific login callback)
      // IMPORTANT: This URL must be whitelisted in Google Cloud Console
      const redirectUrl = window.location.origin + '/auth/login';

      // 3. Construct the full Auth URL
      // Force Google account chooser even when the browser already has an active Google session.
      // PocketBase forwards unknown query params to the OAuth provider, so adding prompt here is safe.
      const authUrl = googleProvider.authUrl + redirectUrl + '&prompt=select_account';

      // 4. Redirect the user
      window.location.href = authUrl;

      // Execution stops here as page unloads
    } catch (error) {
      console.error('Google OAuth init failed:', error);
      this.customToastrService.showError('Google login failed to initialize.');
      throw error;
    }
  }

  // Handle the callback after Google redirects back
  async completeGoogleLogin(code: string, state: string) {
    const savedState = localStorage.getItem('pb_auth_state');
    const codeVerifier = localStorage.getItem('pb_auth_cv');

    if (state !== savedState) {
      throw new Error("State parameters don't match.");
    }

    if (!codeVerifier) {
      throw new Error("Code verifier not found. Please try again.");
    }

    const pb = this.pocketbaseService.getPocketBase();

    // Re-construct the same redirect URL used in init
    const redirectUrl = window.location.origin + '/auth/login';
    console.log('Sending OAuth Code Exchange Request. Redirect URL:', redirectUrl);

    // NOTE: In pocketbase@0.23.0 the 5th param is `createData` and the 6th param is `options` (expand/fields/etc).
    // Passing `{ expand: ... }` as the 5th param ends up as createData and may cause subtle issues.
    const authData = await pb.collection('users').authWithOAuth2Code(
      'google',
      code,
      codeVerifier,
      redirectUrl,
      {},
      { expand: 'organization' }
    );

    // If token is empty, PocketBase didn't establish a session (common when MFA is enabled and not completed).
    if (!authData?.token) {
      console.error('[OAuth] Code exchange returned an empty auth token:', authData);
      throw new Error(
        'Google-Anmeldung fehlgeschlagen: PocketBase hat kein Session-Token zurückgegeben. ' +
        'Wenn in der users-Collection "Multi-factor authentication (MFA)" aktiviert ist, erzwingt PocketBase nach OAuth noch einen zweiten Login-Faktor (z.B. OTP). ' +
        'Für euren Use-Case (Google OAuth ohne extra OTP) muss MFA in der users-Collection deaktiviert sein oder ihr müsst den MFA-Step implementieren.'
      );
    }

    // Clean up
    localStorage.removeItem('pb_auth_state');
    localStorage.removeItem('pb_auth_cv');

    // Ensure signals are up-to-date immediately, even if authStore.onChange fires later.
    this.syncFromAuthStore();

    console.log('OAuth login successful:', authData);
    return authData;
  }
}
